import { LogOut, Radio } from "lucide-react";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  type To,
} from "react-router-dom";
import { useState, type ReactNode } from "react";

import { useSession } from "../api/hooks.js";
import { signOut } from "../api/session.js";
import { Button, buttonVariants } from "../design-system/button.js";
import { cn } from "../design-system/cn.js";
import { MiniPlayer } from "../player/mini-player.js";
import { NowPlayingSheet } from "../player/now-playing-sheet.js";
import { PlayerBar } from "../player/player-bar.js";
import { usePlayer } from "../player/player-provider.js";
import { NAV_ITEMS, type NavItem } from "./navigation.js";

const SHEET_HASH = "#now-playing";

/**
 * One shell, two shapes.
 *
 * Desktop is two persistent regions: a full-height navigation rail and the
 * workspace to its right. The desktop player belongs to that workspace and is
 * fixed to its bottom edge; it never occupies or shortens the navigation rail.
 * Mobile is a scrolling page with its own compact player docked above the tab bar.
 */
export function Shell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { target } = usePlayer();
  const sheetOpen = location.hash === SHEET_HASH && target !== null;

  return (
    <div className="min-h-svh">
      <DesktopNav />

      <div className="lg:pl-(--nav-w)">
        <MobileHeader />
        <main className="app-page mx-auto w-full max-w-[80rem] pt-4 lg:pt-8">
          {children}
        </main>
      </div>

      <PlayerBar />

      <div className="app-dock fixed inset-x-0 bottom-0 z-40 lg:hidden">
        <MiniPlayer
          onExpand={() => {
            void navigate({ hash: SHEET_HASH });
          }}
        />
        <MobileTabs />
      </div>

      {sheetOpen ? (
        <NowPlayingSheet
          onClose={() => {
            void navigate(-1);
          }}
        />
      ) : null}
    </div>
  );
}

function Wordmark({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2.5", className)}>
      <span className="grid size-8 place-items-center rounded-md bg-accent text-accent-ink">
        <Radio aria-hidden="true" className="size-4" />
      </span>
      <span className="text-base font-semibold tracking-tight text-ink">
        Radio
      </span>
    </div>
  );
}

function DesktopNav() {
  return (
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-(--nav-w) flex-col border-r border-border bg-bg-2 lg:flex">
      <div className="px-5 py-6">
        <Wordmark />
      </div>
      <nav className="flex flex-1 flex-col gap-0.5 px-3">
        {NAV_ITEMS.map((item) => (
          <DesktopNavItem key={item.to} item={item} />
        ))}
      </nav>
      <div className="border-t border-border p-3">
        <SessionBadge />
      </div>
    </aside>
  );
}

/**
 * Who is signed in, and the way out.
 *
 * Listening needs nobody, so an anonymous visitor is not nagged — he is offered a way
 * in and nothing more. Signing out returns him to what he was reading rather than to
 * a locked door: the surface an anonymous visitor gets is a real one.
 */
function SessionBadge({ compact = false }: { compact?: boolean }) {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!session) {
    const here = `${location.pathname}${location.search}`;
    return (
      <Link
        className={cn(
          buttonVariants({ size: "small", variant: "secondary" }),
          compact ? undefined : "w-full",
        )}
        to={`/sign-in?next=${encodeURIComponent(here)}`}
      >
        Sign in
      </Link>
    );
  }

  const who = session.email || session.name || "Signed in";

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-xs text-ink-3",
          compact && "max-w-[9rem]",
        )}
        title={who}
      >
        {who}
      </span>
      <Button
        aria-label="Sign out"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void signOut().finally(() => {
            setBusy(false);
            void navigate("/feeds");
          });
        }}
        size="icon-small"
        title="Sign out"
        variant="ghost"
      >
        <LogOut aria-hidden="true" />
      </Button>
    </div>
  );
}

function DesktopNavItem({ item }: { item: NavItem }) {
  const Icon = item.icon;
  const base =
    "flex items-center gap-3 rounded-sm px-3 py-2.5 text-sm font-medium transition-colors";

  return (
    <NavLink
      className={({ isActive }) =>
        cn(
          base,
          isActive
            ? "bg-accent-soft text-accent-text"
            : "text-ink-2 hover:bg-surface hover:text-ink",
        )
      }
      to={item.to as To}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      {item.label}
    </NavLink>
  );
}

function MobileHeader() {
  return (
    <header className="sticky top-0 z-20 border-b border-border bg-bg/90 backdrop-blur lg:hidden">
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <Wordmark />
        <SessionBadge compact />
      </div>
    </header>
  );
}

function MobileTabs() {
  return (
    <nav className="grid grid-cols-5 border-t border-border bg-surface/97 backdrop-blur">
      {NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const base =
          "flex h-(--tab-bar-h) flex-col items-center justify-center gap-1 text-[0.625rem] font-medium";

        return (
          <NavLink
            key={item.to}
            className={({ isActive }) =>
              cn(base, isActive ? "text-accent-text" : "text-ink-3")
            }
            to={item.to as To}
          >
            {({ isActive }) => (
              <>
                <Icon
                  aria-hidden="true"
                  className="size-5"
                  strokeWidth={isActive ? 2.4 : 2}
                />
                {item.label}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}
